import { JSX, useEffect, useRef, useState } from 'react';
import {
  DatasourceTable,
  HeaderCell,
  newTableTheme,
} from '@ardoq/table-legacy';
import styled, { css } from 'styled-components';
import { colors, s16, s24, space } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';
import { CheckCircleIcon } from '@ardoq/icons';
import { TablesSelectionDataSource, TablesSelectionTexts } from './types';
import { FlexBox } from '@ardoq/layout';
import { EmptyState, EmptyStateNoResultIllustration } from '@ardoq/status-ui';
import { WithPopover } from '@ardoq/popovers';
import { type I18nLocale, t } from '@ardoq/i18n';

const Tr = styled.tr<{ $isDisabled?: boolean }>`
  cursor: ${props => (props.$isDisabled ? 'not-allowed' : 'pointer')};

  th:first-child {
    padding-left: ${s24};
  }

  ${props =>
    props.$isDisabled &&
    css`
      opacity: 0.5;
    `}

  ${props =>
    !props.$isDisabled &&
    css`
      &:hover {
        background-color: ${colors.surfaceHover};
        overflow-wrap: break-word;
      }
    `}

  &:not(:hover) td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Td = styled.td`
  height: 40px;
  padding-right: ${space.medium};
  & span {
    max-width: 180px;
  }
`;

const Th = styled.th`
  ${header4};
  padding-bottom: ${s16};
  & > * {
    padding: 0;
  }
`;

const Table = styled(newTableTheme.Table)`
  table-layout: fixed;
  colgroup col:first-child {
    width: 40px;
  }
`;

type TableParams<T extends TablesSelectionDataSource> = {
  i18nLocale: I18nLocale;
  dataSource: T[];
  selectedRowId: string | null;
  onCellClick: (row: T) => void;
  texts: TablesSelectionTexts;
  selectedTables: Record<string, unknown>;
  getSortableColumnProps: (columnsSortBy: string) => {
    headerRender: ({ title }: HeaderCell) => JSX.Element;
  };
  noResultsTitle?: string;
  noResultsDescription?: string;
};

export const TablesTable = <T extends TablesSelectionDataSource>({
  i18nLocale,
  dataSource,
  selectedRowId,
  texts,
  getSortableColumnProps,
  onCellClick,
  selectedTables,
  noResultsTitle,
  noResultsDescription,
}: TableParams<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 100;
  const totalPages = Math.ceil(dataSource.length / rowsPerPage);

  const slicedDataSource = dataSource.slice(0, (currentPage + 1) * rowsPerPage);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const cellStyles = (row: T) =>
    row.name === selectedRowId
      ? {
          backgroundColor: `${colors.surfaceActive}`,
        }
      : {};

  const getRowComponent = (row: T) => {
    return (props: React.ComponentProps<any>) => (
      <WithPopover content={row.isDisabled ? row.disabledTooltip : undefined}>
        <Tr {...props} $isDisabled={row.isDisabled} />
      </WithPopover>
    );
  };

  const handleRowClick = (row: T) => {
    if (!row.isDisabled) {
      onCellClick(row);
    }
  };

  useEffect(() => {
    const tableBody =
      tableContainerRef.current?.querySelector('div:has(table)');

    if (!tableBody) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = () => {
      requestAnimationFrame(() => {
        const scrollTop = tableBody.scrollTop;
        const scrollHeight = tableBody.scrollHeight;
        const clientHeight = tableBody.clientHeight;

        const isScrollingDown = scrollTop > lastScrollTop;

        if (
          isScrollingDown &&
          scrollTop + clientHeight >= scrollHeight - 1500
        ) {
          setCurrentPage(prevPage =>
            prevPage < totalPages ? prevPage + 1 : prevPage
          );
        }

        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
      });
    };

    tableBody.addEventListener('scroll', handleScroll);
    return () => {
      if (tableBody) {
        tableBody.removeEventListener('scroll', handleScroll);
      }
    };
  }, [tableContainerRef, totalPages]);

  return (
    <div ref={tableContainerRef}>
      <DatasourceTable
        renderEmptyTable={() => (
          <EmptyState
            title={
              noResultsTitle ??
              t(
                i18nLocale,
                'integrations:tablesSelection.noResultsTitle',
                { label: texts.labelColumnTitle.toLowerCase() },
                'No {{label}} found'
              )
            }
            description={
              noResultsDescription ??
              t(
                i18nLocale,
                'integrations:tablesSelection.noResultsDescription',
                { label: texts.labelColumnTitle.toLowerCase() },
                "There's not {{label}} to show with current selection"
              )
            }
            media={<EmptyStateNoResultIllustration />}
          />
        )}
        onRowClick={handleRowClick}
        getRowComponent={getRowComponent}
        style={{
          height: `600px`,
          overflowY: 'auto',
          overflowAnchor: 'auto',
        }}
        components={{
          ...newTableTheme,
          HeadTr: Tr,
          Td,
          Th,
          Table,
        }}
        fixedHeader={true}
        dataSource={slicedDataSource}
        rowStyle={cellStyles}
        columns={[
          {
            dataIndex: 'id',
            valueRender: (id: string) =>
              !!selectedTables[id] && (
                <FlexBox
                  width="full"
                  height="full"
                  align="center"
                  justify="center"
                >
                  <CheckCircleIcon color={colors.iconSuccess} />
                </FlexBox>
              ),
          },
          {
            dataIndex: 'label',
            title: texts.labelColumnTitle,
            ...getSortableColumnProps('label'),
          },
          {
            dataIndex: 'name',
            title: texts.nameColumnTitle,
            headerStyle: { minWidth: '150px' },
            ...getSortableColumnProps('name'),
          },
        ]}
      />
    </div>
  );
};
